import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Fade, Zoom } from "@mui/material";
import OrderDetailsSection from "./components/OrderDetailsSection";
import ThermalPrinterTest from "src/print-configuration/PrintConfigration";
import { useOrderDetails } from "./hooks/useOrderDetails";
import PaymentDetailsSection from "./components/PaymentDetailsSection";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import useResPrintData from "./useResPrintData";
import { Order } from "src/shared/services/interfacev2";
import { putOrder } from "src/shared/services/servicesv2";
import { pageTitle } from "src/shared/utils/brand";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { processCardPayment } from "src/paymentGatway/useCardPayment";
import { useUser } from "src/shared/context/UserContext";
import { SplitPayment } from "./components/paymentSection/PaymentMethodSelector";

const Respayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userConfigration } = useUser();

  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentParcelFee, setCurrentParcelFee] = useState<number>(0);
  const [currentDeliveryFee, setCurrentDeliveryFee] = useState<number>(0);
  const [currentDiscount, setCurrentDiscount] = useState<number>(0);
  const [splitPayment, setSplitPayment] = useState<SplitPayment>({ cashAmount: 0,cardAmount: 0,});

  const { order, setOrder, loading, refetchOrder } = useOrderDetails(id);

  const printData = useResPrintData(order);

  useEffect(() => {
    document.title = pageTitle("Payment");
  }, []);

  // Initialize current fees from order when it loads
  useEffect(() => {
    if (order?.orders?.[0]) {
      setCurrentParcelFee(Number(order.orders[0].parcelFee ?? 0));
      setCurrentDeliveryFee(Number(order.orders[0].deliveryFee ?? 0));
      setCurrentDiscount(Number(order.orders[0].discount ?? 0));
    }
  }, [order]);

  const txn = order?.orders?.[0];

  const guest = txn?.guest || 0;
  const orderAmount = Number(txn?.orderAmount || 0);

  // Calculate total with real-time fees
  const calculatedTotal = orderAmount + currentParcelFee + currentDeliveryFee - currentDiscount;
  const totalWithTip = calculatedTotal + tip;
  const finalAmount = calculatedTotal + tip;

  const handleCustomTipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomTip(value);
    setTip(Number(value) || 0);
  };

  const handleTipChange = (amount: number) => {
    setTip(amount);
    setCustomTip("");
  };

  // Add this function in Respayment component
  const handleFeeChange = (fees: { parcelFee: number; deliveryFee: number; discount: number }) => {
    setCurrentParcelFee(fees.parcelFee);
    setCurrentDeliveryFee(fees.deliveryFee);
    setCurrentDiscount(fees.discount);
  };

  // NEW: when payment method changes, reset/seed the split state
  const handlePaymentMethodChange = (method: string) => {
    setPaymentMethod(method);
    if (method === "Split") {
      // Default: entire amount on card, cash = 0 — user can adjust
      setSplitPayment({
        cashAmount: 0,
        cardAmount: 0,
      });
    } else {
      setSplitPayment({ cashAmount: 0, cardAmount: 0 });
    }
  };

  const now = new Date();
  const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");

  const handlePayNow = async () => {
    if (!order || !txn || !id) return;

    setIsProcessing(true);

    // Build per-leg payment list. For split, we record two entries.
    const splitPayments =
      paymentMethod === "Split"
        ? [
            { paymentMethod: "Cash", receivedAmount: splitPayment.cashAmount },
            { paymentMethod: "Card", receivedAmount: splitPayment.cardAmount },
          ].filter((p) => p.receivedAmount > 0) // drop zero legs
        : [{ paymentMethod, receivedAmount: totalWithTip }];

    const paymentPayload: any = {
      customerId: order.orders[0].customerId,
      orderStatus: "Completed",
      finalAmount: finalAmount,
      remainingAmount: 0,
      outstandingBalance: totalWithTip,
      newOrderAmount: 0,
      orderDate: localDate,
      orderTime: now.toTimeString().split(" ")[0],
      date: localDate,
      time: now.toTimeString().split(" ")[0],
      // For split: send array of legs. For single: single object (backward compat).
      payments:
        paymentMethod === "Split" ? splitPayments : splitPayments[0],
      paymentHistory: splitPayments.map((p) => ({
        paymentMethod: p.paymentMethod,
        receivedAmount: p.receivedAmount,
      })),
      tip: tip,
      parcelFee: currentParcelFee,
      deliveryFee: currentDeliveryFee,
      discount: currentDiscount,
    };

    const needsCardProcessing =
      Boolean(userConfigration?.cardPayment) &&
      (paymentMethod === "Card" ||
        (paymentMethod === "Split" && splitPayment.cardAmount > 0));

    // ---------- Card (or split-with-card) path ----------
    if (needsCardProcessing) {
      const cardAmountToCharge =
        paymentMethod === "Split" ? splitPayment.cardAmount : totalWithTip;

      await processCardPayment({
        amount: cardAmountToCharge,
        description: `Order payment for ${
          order?.orders[0]?.customerId || "Unknown Customer"
        }`,
        onSuccess: async (paymentDetails) => {
          const paysucess = { ...paymentPayload, paymentDetails };
          try {
            await putOrder(id, paysucess);
            notifySuccess("Payment completed successfully!");

            // ✅ Refetch order to get the latest data including tip
            await refetchOrder();

            // Then update local state
            setOrder((prev): Order | null => {
              if (!prev) return null;
              return {
                ...prev,
                orders: prev.orders.map((o, i) => (i === 0 ? { ...o, orderStatus: "Completed", remainingAmount: 0, tip: tip } : o)),
              };
            });

            setShowPrint(true);
            setTimeout(() => {
              setTimeout(() => {
                navigate("/v1/Reshome");
              }, 1000);
            }, 500);
          } catch (error) {
            console.error(error);
            notifyError("Failed to complete payment");
          } finally {
            setIsProcessing(false);
          }
        },
      });
      return;
    }

    // ---------- Cash / Split-with-no-card path ----------
    try {
      await putOrder(id, paymentPayload);
      notifySuccess("Payment completed successfully!");

      // ✅ Refetch order to get the latest data including tip
      await refetchOrder();

      // Then update local state
      setOrder((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          orders: prev.orders.map((o, i) => (i === 0 ? { ...o, orderStatus: "Completed", remainingAmount: 0, tip: tip } : o)),
        };
      });

      setShowPrint(true);
      setTimeout(() => {
        setTimeout(() => {
          navigate("/v1/Reshome");
        }, 1000);
      }, 500);
    } catch (error) {
      console.error(error);
      notifyError("Failed to complete payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditConfirm = async ({ parcelFee, deliveryFee, discount }: { parcelFee: number; deliveryFee: number; discount: number }) => {
    if (!order || !id) return;

    const finalamount = Number(order.orders[0].orderAmount) + parcelFee + deliveryFee - discount + tip;

    try {
      const payload = {
        customerId: order.orders[0].customerId,
        orderStatus: order.orders[0].status,
        parcelFee,
        deliveryFee,
        discount,
        remainingAmount: finalamount,
        finalAmount: finalamount,
        tip: tip,
      };

      await putOrder(id, payload);
      await refetchOrder();
      setCurrentParcelFee(parcelFee);
      setCurrentDeliveryFee(deliveryFee);
      setCurrentDiscount(discount);

      notifySuccess("Order updated successfully!");
    } catch (err) {
      console.error(err);
      notifyError("Failed to update order");
    }
  };

  return (
    <div className="flex flex-col h-[92vh] overflow-hidden">
      <PageTitle title="- Payment" />

      <Fade in={!loading} timeout={500}>
        <div className="flex flex-1 flex-col lg:flex-row p-1 gap-2 h-[calc(100vh-4rem)] overflow-hidden">
          <Zoom in={!loading} style={{ transitionDelay: "200ms" }}>
            <div className="w-full lg:w-[63%] h-full overflow-hidden rounded-lg">
              <OrderDetailsSection order={order} />
            </div>
          </Zoom>

          <div className="thin-scrollbar w-full lg:w-[37%] h-full overflow-y-auto">
            <PaymentDetailsSection
              order={order}
              tip={tip}
              customTip={customTip}
              paymentMethod={paymentMethod}
              guess={guest}
              total={totalWithTip}
              onTipChange={handleTipChange}
              onCustomTipChange={handleCustomTipChange}
              onPayNow={handlePayNow}
              onPaymentMethodChange={handlePaymentMethodChange}
              isProcessing={isProcessing}
              onConfirmEdit={handleEditConfirm}
              hasUnsavedChanges={hasUnsavedChanges}
              setHasUnsavedChanges={setHasUnsavedChanges}
              onFeeChange={handleFeeChange}
              splitPayment={splitPayment}
              onSplitPaymentChange={setSplitPayment}
            />
          </div>
        </div>
      </Fade>

      {showPrint && printData && <ThermalPrinterTest PrintData={printData} isClosed={() => setShowPrint(false)} openPrint="true" />}

      <ToastContainer autoClose={2000} position="top-right" />
      {loading && <LoadingScreen />}
    </div>
  );
};

export default Respayment;